import React, { useCallback, useMemo, useState } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { theme } from '@sg/garnish';

import {
  type FeatureFlagKey,
  type FeatureFlags,
  useLaunchDarkly,
} from '@order/LaunchDarkly';

import {
  FeatureFlagsClearButton,
  FeatureFlagsListFilterField,
  FeatureFlagsListHeader,
} from './components';
import { FeatureFlag } from './FeatureFlag';
import { FeatureFlagEditor } from './FeatureFlagEditor';

// ─────────────────────────────────────────────────────────────────────────────

export const FeatureFlagSwitcher = () => {
  const {
    flagOverrides,
    defaultFlags,
    overrideFeatureFlag,
    clearFeatureFlagOverrides,
  } = useLaunchDarkly();

  // ─── State ───────────────────────────────────────────────────────────

  const [searchQuery, setSearchQuery] = useState('');

  // ─── Derived Data ────────────────────────────────────────────────────

  const [sortedFlags, setSortedFlags] = useState(
    sortFeatureFlags(defaultFlags, flagOverrides),
  );

  const filteredFlags = useMemo(() => {
    return sortedFlags.filter((flag) =>
      flag.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, sortedFlags]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const updateFeatureFlag = useCallback(
    (flagName: FeatureFlagKey, flagValue: boolean) => {
      overrideFeatureFlag(flagName, flagValue);
    },
    [overrideFeatureFlag],
  );

  const clearOverrides = useCallback(() => {
    clearFeatureFlagOverrides();
    setSortedFlags(sortFeatureFlags(defaultFlags, {}));
  }, [clearFeatureFlagOverrides, defaultFlags]);

  const hasOverrides = Object.keys(flagOverrides).length > 0;

  // ─── Editing Feature Flag ────────────────────────────────────────────

  const {
    featureFlagToEdit,
    newFlagValue,
    editFlag,
    updateEditValue,
    cancelEdit,
  } = useFeatureFlagEditing(defaultFlags, flagOverrides);

  const confirmFeatureFlagEdit = useCallback(() => {
    if (!featureFlagToEdit) return;

    overrideFeatureFlag(featureFlagToEdit, getFlagValue(newFlagValue));
    cancelEdit();
  }, [featureFlagToEdit, newFlagValue, overrideFeatureFlag, cancelEdit]);

  if (featureFlagToEdit) {
    return (
      <View style={styles.container}>
        <FeatureFlagsListHeader.Container>
          <FeatureFlagsListHeader.ControlsContainer>
            <FeatureFlagsListHeader.Title />
            <FeatureFlagsClearButton
              onPress={clearOverrides}
              isDisabled={!hasOverrides}
            />
          </FeatureFlagsListHeader.ControlsContainer>
        </FeatureFlagsListHeader.Container>

        <View style={styles.contentContainer}>
          <FeatureFlagEditor
            featureFlagToEdit={featureFlagToEdit}
            newFlagValue={newFlagValue}
            updateEditValue={updateEditValue}
            confirmFeatureFlagEdit={confirmFeatureFlagEdit}
            cancelEdit={cancelEdit}
          />
        </View>
      </View>
    );
  }

  // ─── Listing Feature Flags ───────────────────────────────────────────

  return (
    <View style={styles.container}>
      <FeatureFlagsListHeader.Container>
        <FeatureFlagsListHeader.ControlsContainer>
          <FeatureFlagsListHeader.Title />
          <FeatureFlagsClearButton
            onPress={clearOverrides}
            isDisabled={!hasOverrides}
          />
        </FeatureFlagsListHeader.ControlsContainer>

        <FeatureFlagsListFilterField
          value={searchQuery}
          onChange={setSearchQuery}
        />
      </FeatureFlagsListHeader.Container>

      <FlatList
        contentContainerStyle={styles.contentContainer}
        data={filteredFlags}
        keyExtractor={(featureFlag) => featureFlag}
        renderItem={({ item: featureFlag }) => {
          const flagKey = featureFlag as FeatureFlagKey;

          return (
            <FeatureFlag
              flagKey={flagKey}
              updateFeatureFlag={updateFeatureFlag}
              editFeatureFlag={editFlag}
            />
          );
        }}
      />
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Sorts the flags alphabetically and then by value, then by overwritten or not.
function sortFeatureFlags(
  initialFeatureFlags: FeatureFlags,
  overrides: Partial<FeatureFlags>,
) {
  return Object.keys(initialFeatureFlags ?? {})
    .sort(sortFeatureFlagsAlphabetically)
    .sort(sortFeatureFlagsByOverrides(overrides));
}

function sortFeatureFlagsAlphabetically(f1: string, f2: string) {
  return f1.localeCompare(f2);
}

function sortFeatureFlagsByOverrides(overrides: Partial<FeatureFlags>) {
  return (featureFlag1: string, featureFlag2: string) => {
    const key1 = featureFlag1 as FeatureFlagKey;
    const key2 = featureFlag2 as FeatureFlagKey;

    const override1 = overrides?.[key1];
    const override2 = overrides?.[key2];

    const hasOverride1 = override1 !== undefined;
    const hasOverride2 = override2 !== undefined;

    const isEqualValue = hasOverride1 === hasOverride2;

    if (isEqualValue) return 0;

    return hasOverride1 ? -1 : 1;
  };
}

function useFeatureFlagEditing(
  initialFeatureFlags: FeatureFlags,
  overrides: ReturnType<typeof useLaunchDarkly>['flagOverrides'],
) {
  const [featureFlagToEdit, setEditingFeatureFlag] =
    useState<FeatureFlagKey | null>(null);
  const [newFlagValue, setNewFlagValue] = useState<unknown>('');

  const editFlag = useCallback(
    (flagName: FeatureFlagKey) => {
      setEditingFeatureFlag(flagName);
      setNewFlagValue(overrides[flagName] ?? initialFeatureFlags?.[flagName]);
    },
    [initialFeatureFlags, overrides],
  );

  const updateEditValue = useCallback((flagValue: string) => {
    setNewFlagValue(flagValue);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingFeatureFlag(null);
    setNewFlagValue('');
  }, []);

  return {
    featureFlagToEdit,
    newFlagValue,
    editFlag,
    updateEditValue,
    cancelEdit,
  };
}

function getFlagValue(flagValue: unknown) {
  try {
    return JSON.parse(flagValue as string);
  } catch {
    return flagValue;
  }
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    gap: theme.spacing['2'],
    minHeight: 320,
    paddingHorizontal: theme.spacing['6'],
    paddingVertical: theme.spacing['4'],
  },
});
